import type { ButtonProps } from '@components/molecules';
import { Button, ButtonSize, ButtonVariant, BackButton } from '@components/molecules';
import { cn, type CompositionProp } from '@utils';
import {
	DialogContent,
	DialogContentPrimitiveProps,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../dialog';

type DialogDataGridLayoutProps = React.PropsWithChildren<
	{
		icon?: React.ReactNode;
		title: CompositionProp;
		description?: CompositionProp;
		footerDescription?: CompositionProp;
		primaryButton: CompositionProp;
		secondaryButton?: CompositionProp;
		primaryButtonProps?: Omit<ButtonProps, 'type' | 'onClick' | 'variant'>;
		secondaryButtonProps?: Omit<ButtonProps, 'type' | 'onClick' | 'variant'>;
		onPrimaryAction: () => void;
		onSecondaryAction?: () => void;
		onBackAction?: () => void;
		closeIcon?: boolean;
		sectionDivider?: boolean;
	} & DialogContentPrimitiveProps
>;

export const DialogDataGridLayout: React.FC<DialogDataGridLayoutProps> = ({
	icon,
	title,
	description,
	children,
	footerDescription,
	primaryButton,
	secondaryButton,
	primaryButtonProps,
	secondaryButtonProps,
	onPrimaryAction,
	onSecondaryAction,
	onBackAction,
	closeIcon = true,
	sectionDivider = true,
	className,
	...props
}) => {
	const titleComponent = typeof title === 'string' ? <DialogTitle>{title}</DialogTitle> : (title as React.ReactNode);

	const descriptionComponent =
		typeof description === 'string' ? (
			<DialogDescription>{description}</DialogDescription>
		) : (
			(description as React.ReactNode)
		);

	const footerDescriptionComponent =
		typeof footerDescription === 'string' ? (
			<p className="text-sm text-secondary">{footerDescription}</p>
		) : (
			(footerDescription as React.ReactNode)
		);

	const primaryButtonComponent =
		typeof primaryButton === 'string' ? (
			<Button {...primaryButtonProps} size={ButtonSize.SM} type="submit" onClick={onPrimaryAction}>
				{primaryButton}
			</Button>
		) : (
			(primaryButton as React.ReactNode)
		);

	const secondaryButtonComponent =
		typeof secondaryButton === 'string' ? (
			<Button
				{...secondaryButtonProps}
				variant={ButtonVariant.SECONDARY}
				size={ButtonSize.SM}
				onClick={onSecondaryAction}
			>
				{secondaryButton}
			</Button>
		) : (
			(secondaryButton as React.ReactNode)
		);

	return (
		<DialogContent
			backButtonComponent={onBackAction ? <BackButton onAction={onBackAction}>Back</BackButton> : undefined}
			closeIcon={closeIcon}
			className={cn('max-h-[90vh] w-[620px] sm:max-w-[95vw]', className)}
			{...props}
		>
			<div className={sectionDivider ? 'divide-y divide-borderSeparator' : ''}>
				<DialogHeader className="shrink-0 justify-items-center pb-4">
					<div className="self-center">{icon && icon}</div>
					{titleComponent}
					{descriptionComponent}
				</DialogHeader>
				<div className="max-h-[60vh] shrink overflow-y-auto px-10 pb-4 pt-4">{children}</div>
				<DialogFooter className="flex shrink-0 flex-col items-center justify-between gap-2 md:flex-row md:items-start md:space-x-4">
					<div className="text-center md:text-left">{footerDescription && footerDescriptionComponent}</div>
					<div className="flex w-full flex-col justify-center space-y-2 md:w-auto md:flex-row md:space-x-4 md:space-y-0">
						{onSecondaryAction && secondaryButtonComponent}
						{primaryButtonComponent}
					</div>
				</DialogFooter>
			</div>
		</DialogContent>
	);
};
